/**
 * Data display: KPI cards with count-up and sparklines, skeletons, empty
 * states, id chips with copy, avatars and key/value lists. Re-exported from
 * `Ui.tsx`; screens import from there.
 */
import type { CSSProperties, MouseEvent, ReactNode } from 'react'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { ArrowDownRight, ArrowUpRight, Check, Copy, Minus } from 'lucide-react'

import { decimalsOf, formatNumber, short, toNumber } from './format'
import { Spinner, cx, toneClass, useCountUp, type Tone } from './primitives'
import styles from './Data.module.css'

/* ------------------------------------------------------------------------ */
/* count-up                                                                 */
/* ------------------------------------------------------------------------ */

export interface CountUpProps {
  /** A number or a decimal string from the API; anything else is shown as is. */
  value: number | string | null | undefined
  /** Formats the running number (default: ru-RU with the value's own decimals). */
  format?: (value: number) => string
  /** Fixed decimals for the default format (default: those of the value string). */
  decimals?: number
  /** Animation length, ms (default 600). */
  duration?: number
  className?: string
}

/** A figure that counts up to its value on mount and on change (mono, tabular); static under reduced motion. */
export function CountUp({ value, format, decimals, duration = 600, className }: CountUpProps): JSX.Element {
  const target = toNumber(value)
  const places = decimals ?? decimalsOf(value)
  const shown = useCountUp(target, { duration })
  const render = format ?? ((n: number) => formatNumber(n, places))
  const text = target === null ? (value === null || value === undefined || value === '' ? '—' : String(value)) : render(shown ?? target)
  return <span className={cx(styles.num, className)}>{text}</span>
}

/* ------------------------------------------------------------------------ */
/* sparkline                                                                */
/* ------------------------------------------------------------------------ */

export interface SparklineProps {
  /** Real series only, oldest first; fewer than two points renders nothing. */
  data: readonly number[]
  /** Stroke colour; default accent, or down when the series ends lower than it starts and `autoTone` is on. */
  tone?: Tone
  /** Pick accent/down from the direction of the series (default true when `tone` is not given). */
  autoTone?: boolean
  /** Drawn height in px (the width follows the container). */
  height?: number
  /** Soft gradient under the line (default true). */
  area?: boolean
  /** Accessible summary, e.g. «Выводы за 14 дней». */
  label?: string
  className?: string
}

const SPARK_W = 120

/** A small smoothed line (with a gradient area and an end dot) drawn in 600 ms; stretches to its container. */
export function Sparkline({ data, tone, autoTone = true, height = 36, area = true, label, className }: SparklineProps): JSX.Element | null {
  const gradientId = useId().replace(/:/g, '')
  const geometry = useMemo(() => {
    const points = data.filter((v) => Number.isFinite(v))
    if (points.length < 2) return null
    const min = Math.min(...points)
    const max = Math.max(...points)
    const pad = 3
    const h = height
    const xy = points.map((v, i) => {
      const x = (i / (points.length - 1)) * SPARK_W
      const y = max === min ? h / 2 : pad + (1 - (v - min) / (max - min)) * (h - pad * 2)
      return [x, y] as const
    })
    // Catmull-Rom to cubic Bézier, control points clamped inside the box.
    const clampY = (y: number): number => Math.max(pad / 2, Math.min(h - pad / 2, y))
    let line = `M${xy[0][0].toFixed(2)},${xy[0][1].toFixed(2)}`
    for (let i = 0; i < xy.length - 1; i++) {
      const p0 = xy[i - 1] ?? xy[i]
      const p1 = xy[i]
      const p2 = xy[i + 1]
      const p3 = xy[i + 2] ?? p2
      const c1x = p1[0] + (p2[0] - p0[0]) / 6
      const c1y = clampY(p1[1] + (p2[1] - p0[1]) / 6)
      const c2x = p2[0] - (p3[0] - p1[0]) / 6
      const c2y = clampY(p2[1] - (p3[1] - p1[1]) / 6)
      line += ` C${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`
    }
    const fill = `${line} L${SPARK_W},${h} L0,${h} Z`
    const last = xy[xy.length - 1]
    return { line, fill, last, falling: points[points.length - 1] < points[0] }
  }, [data, height])
  if (!geometry) return null
  const resolved: Tone = tone ?? (autoTone && geometry.falling ? 'down' : 'accent')
  return (
    <span className={cx(styles.spark, toneClass(resolved), className)} style={{ height }} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <svg viewBox={`0 0 ${SPARK_W} ${height}`} preserveAspectRatio="none" width="100%" height={height}>
        {area ? (
          <>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" style={{ stopColor: 'var(--tone)', stopOpacity: 0.28 }} />
                <stop offset="100%" style={{ stopColor: 'var(--tone)', stopOpacity: 0 }} />
              </linearGradient>
            </defs>
            <path className={styles.sparkArea} d={geometry.fill} fill={`url(#${gradientId})`} />
          </>
        ) : null}
        <path className={styles.sparkLine} d={geometry.line} pathLength={1} fill="none" vectorEffect="non-scaling-stroke" />
      </svg>
      <span className={styles.sparkDot} style={{ left: `${(geometry.last[0] / SPARK_W) * 100}%`, top: `${(geometry.last[1] / height) * 100}%` }} />
    </span>
  )
}

/* ------------------------------------------------------------------------ */
/* stat card                                                                */
/* ------------------------------------------------------------------------ */

export interface StatCardProps {
  /** Eyebrow above the figure, e.g. «Выводы ждут решения». */
  label: ReactNode
  /** The figure: a number or decimal string counts up; other text is shown as is. */
  value: number | string | null | undefined
  /** Formats the figure (default: ru-RU with the value's own decimals). */
  format?: (value: number) => string
  decimals?: number
  /** Small unit after the figure, e.g. «USDT». */
  unit?: ReactNode
  /** Change to show in the chip; sign picks the tone. */
  delta?: number | null
  /** Appended to the delta (default « %»). */
  deltaSuffix?: string
  deltaFormat?: (value: number) => string
  /** When a rise is bad (errors, queue length): up turns red, down turns green. */
  invertDelta?: boolean
  /** Words after the chip, e.g. «за сутки». */
  deltaLabel?: ReactNode
  /** A real series for the sparkline (never invent one). */
  sparkline?: readonly number[]
  /** Tints the icon tile and the default sparkline. */
  tone?: Tone
  icon?: ReactNode
  /** A caption under the figure. */
  hint?: ReactNode
  /** Skeleton in place of the figure. */
  loading?: boolean
  /** md: 28 px figure · lg: 36 px. */
  size?: 'md' | 'lg'
  /** Makes the whole card a button. */
  onClick?: () => void
  /** Aria label for the clickable card. */
  actionLabel?: string
  /** Turn off the count-up (e.g. for a figure that ticks every second). */
  countUp?: boolean
  className?: string
  /** Extra content under the figure (a Progress bar, a badge row). */
  children?: ReactNode
}

function formatDelta(delta: number, suffix: string): string {
  const abs = Math.abs(delta).toLocaleString('ru-RU', { maximumFractionDigits: Math.abs(delta) < 10 ? 1 : 0 })
  return `${delta > 0 ? '+' : delta < 0 ? '−' : ''}${abs}${suffix}`
}

/** A KPI tile: eyebrow, mono figure counting up, optional unit, delta chip, sparkline and icon. */
export function StatCard({
  label,
  value,
  format,
  decimals,
  unit,
  delta,
  deltaSuffix = ' %',
  deltaFormat,
  invertDelta = false,
  deltaLabel,
  sparkline,
  tone = 'accent',
  icon,
  hint,
  loading = false,
  size = 'md',
  onClick,
  actionLabel,
  countUp = true,
  className,
  children,
}: StatCardProps): JSX.Element {
  const hasDelta = delta !== null && delta !== undefined && Number.isFinite(delta)
  const good = hasDelta ? (invertDelta ? delta < 0 : delta > 0) : false
  const deltaTone: Tone = !hasDelta || delta === 0 ? 'neutral' : good ? 'accent' : 'down'
  const figure = countUp ? (
    <CountUp value={value} format={format} decimals={decimals} />
  ) : (
    <span className={styles.num}>{toNumber(value) === null ? (value ?? '—') : (format ?? ((n: number) => formatNumber(n, decimals ?? decimalsOf(value))))(toNumber(value) as number)}</span>
  )
  const inner = (
    <>
      <div className={styles.statHead}>
        <span className={styles.eyebrow}>{label}</span>
        {icon ? <span className={cx(styles.statIcon, toneClass(tone))}>{icon}</span> : null}
      </div>
      <div className={styles.statBody}>
        <div className={styles.statFigureWrap}>
          {loading ? (
            <Skeleton width={size === 'lg' ? 140 : 96} height={size === 'lg' ? 32 : 26} />
          ) : (
            <span className={cx(styles.statFigure, size === 'lg' ? styles.statFigureLg : '')}>
              {figure}
              {unit ? <span className={styles.statUnit}>{unit}</span> : null}
            </span>
          )}
          {hasDelta || deltaLabel || hint ? (
            <div className={styles.statMeta}>
              {hasDelta ? (
                <span className={cx(styles.delta, toneClass(deltaTone))}>
                  {delta > 0 ? <ArrowUpRight aria-hidden="true" /> : delta < 0 ? <ArrowDownRight aria-hidden="true" /> : <Minus aria-hidden="true" />}
                  {deltaFormat ? deltaFormat(delta) : formatDelta(delta, deltaSuffix)}
                </span>
              ) : null}
              {deltaLabel ? <span className={styles.statHint}>{deltaLabel}</span> : null}
              {hint && !deltaLabel ? <span className={styles.statHint}>{hint}</span> : null}
            </div>
          ) : null}
          {hint && deltaLabel ? <div className={styles.statHint}>{hint}</div> : null}
        </div>
        {sparkline && sparkline.length > 1 && !loading ? (
          <Sparkline data={sparkline} className={styles.statSpark} tone={tone === 'accent' ? undefined : tone} />
        ) : null}
      </div>
      {children ? <div className={styles.statExtra}>{children}</div> : null}
    </>
  )
  return onClick ? (
    <button type="button" className={cx(styles.stat, styles.statButton, className)} onClick={onClick} aria-label={actionLabel}>
      {inner}
    </button>
  ) : (
    <div className={cx(styles.stat, className)}>{inner}</div>
  )
}

/* ------------------------------------------------------------------------ */
/* skeleton                                                                 */
/* ------------------------------------------------------------------------ */

export interface SkeletonProps {
  /** CSS width (px number or any length), default 100%. */
  width?: number | string
  /** Height, default 12 px. */
  height?: number | string
  /** Corner radius; default 6 px (a circle with `circle`). */
  radius?: number | string
  circle?: boolean
  className?: string
  style?: CSSProperties
}

/** A shimmering placeholder block (1.4 s sweep; still under reduced motion). */
export function Skeleton({ width = '100%', height = 12, radius, circle = false, className, style }: SkeletonProps): JSX.Element {
  return (
    <span
      className={cx(styles.skeleton, className)}
      aria-hidden="true"
      style={{ width, height, borderRadius: circle ? '50%' : radius, ...style }}
    />
  )
}

/** Several text-line skeletons, the last one shorter. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }): JSX.Element {
  return (
    <span className={cx(styles.skeletonText, className)} aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} width={i === lines - 1 && lines > 1 ? '62%' : '100%'} />
      ))}
    </span>
  )
}

/* ------------------------------------------------------------------------ */
/* empty states                                                             */
/* ------------------------------------------------------------------------ */

const LOADING_TEXT = /^(Загрузка|Загружа|Ищем|Проверяем)/

/**
 * The plain message line inside a panel or table («Заявок нет»,
 * «Загрузка…», an error text). A text starting with «Загрузка»/«Ищем» gets a
 * spinner by itself; `loading`, `tone="error"` and `icon` make it explicit.
 */
export function Empty({ children, icon, tone = 'neutral', loading }: { children: ReactNode; icon?: ReactNode; tone?: 'neutral' | 'error'; loading?: boolean }): JSX.Element {
  const busy = loading ?? (typeof children === 'string' && LOADING_TEXT.test(children))
  return (
    <div className={cx(styles.empty, tone === 'error' ? styles.emptyError : '')} aria-busy={busy || undefined} role={tone === 'error' ? 'alert' : undefined}>
      {busy ? <Spinner size={14} className={styles.emptySpinner} /> : icon ? <span className={styles.emptyInlineIcon}>{icon}</span> : null}
      <span>{children}</span>
    </div>
  )
}

export interface EmptyStateProps {
  /** An 18–20 px Lucide icon, shown in a tile. */
  icon?: ReactNode
  title: ReactNode
  description?: ReactNode
  /** A button (or two) under the text. */
  action?: ReactNode
  /** Less padding, for a table or a side panel. */
  compact?: boolean
  /** error: red tile; success: accent tile. */
  tone?: 'neutral' | 'error' | 'success'
  className?: string
}

/** A centred empty or error state: icon tile, title, one line of help, an action. */
export function EmptyState({ icon, title, description, action, compact = false, tone = 'neutral', className }: EmptyStateProps): JSX.Element {
  return (
    <div className={cx(styles.emptyState, compact ? styles.emptyStateCompact : '', className)} role={tone === 'error' ? 'alert' : undefined}>
      {icon ? (
        <span className={cx(styles.emptyIcon, tone === 'error' ? toneClass('down') : tone === 'success' ? toneClass('accent') : styles.emptyIconNeutral)}>{icon}</span>
      ) : null}
      <div className={styles.emptyTitle}>{title}</div>
      {description ? <div className={styles.emptyDescription}>{description}</div> : null}
      {action ? <div className={styles.emptyAction}>{action}</div> : null}
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/* id chip                                                                  */
/* ------------------------------------------------------------------------ */

/** Copies text to the clipboard (with a fallback for non-secure origins); resolves to success. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const area = document.createElement('textarea')
    area.value = text
    area.setAttribute('readonly', '')
    area.style.position = 'fixed'
    area.style.opacity = '0'
    const host = document.querySelector('dialog[open]') ?? document.body
    host.appendChild(area)
    area.select()
    const ok = document.execCommand('copy')
    area.remove()
    return ok
  } catch {
    return false
  }
}

export interface IdChipProps {
  /** The full id, hash or address; «—» when empty. */
  value: string | null | undefined
  /** Characters kept at the start / end (default 8 / 6). */
  head?: number
  tail?: number
  /** Show the whole value instead of head…tail. */
  full?: boolean
  /** Show the copy button (default true). */
  copy?: boolean
  /** What the value is, for the copy button's label: «хеш», «адрес» (default «значение»). */
  label?: string
  /** Plain text colour instead of the chip frame. */
  bare?: boolean
  className?: string
}

/** A mono id/hash/address, shortened in the middle, with a copy button and «Скопировано» feedback; clicks do not reach the row. */
export function IdChip({ value, head = 8, tail = 6, full = false, copy = true, label = 'значение', bare = false, className }: IdChipProps): JSX.Element {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(timer.current), [])
  if (!value) return <span className={cx(styles.num, styles.idEmpty, className)}>—</span>
  const text = full ? value : short(value, head, tail)
  const onCopy = async (event: MouseEvent): Promise<void> => {
    event.stopPropagation()
    event.preventDefault()
    const ok = await copyText(value)
    setState(ok ? 'copied' : 'failed')
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setState('idle'), 1400)
  }
  return (
    <span className={cx(styles.idChip, bare ? styles.idBare : '', copy ? '' : styles.idNoCopy, className)} title={value}>
      <span className={styles.idText}>{text}</span>
      {copy ? (
        <button
          type="button"
          className={styles.idCopy}
          onClick={(event) => void onCopy(event)}
          aria-label={state === 'copied' ? 'Скопировано' : `Скопировать ${label}`}
          data-state={state}
        >
          {state === 'copied' ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
        </button>
      ) : null}
      {state !== 'idle' ? (
        <span className={cx(styles.idToast, state === 'failed' ? styles.idToastFailed : '')} role="status">
          {state === 'copied' ? 'Скопировано' : 'Не удалось скопировать'}
        </span>
      ) : null}
    </span>
  )
}

/* ------------------------------------------------------------------------ */
/* avatar                                                                   */
/* ------------------------------------------------------------------------ */

const AVATAR_TONES: Tone[] = ['accent', 'info', 'warning', 'neutral']

/** Two letters from a name or an email («operator@…» → «OP», «anna.k@…» → «AK»). */
export function initials(name: string | null | undefined): string {
  if (!name) return '?'
  const local = name.includes('@') ? name.slice(0, name.indexOf('@')) : name
  const parts = local.split(/[^\p{L}\p{N}]+/u).filter(Boolean)
  if (parts.length === 0) return '?'
  const letters = parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)
  return letters.toUpperCase()
}

/** A round initials avatar; the tone is stable per name. */
export function Avatar({ name, size = 32, tone, className, title }: { name: string | null | undefined; size?: number; tone?: Tone; className?: string; title?: string }): JSX.Element {
  const picked = useMemo<Tone>(() => {
    if (tone) return tone
    let hash = 0
    for (const ch of name ?? '') hash = (hash * 31 + ch.charCodeAt(0)) | 0
    return AVATAR_TONES[Math.abs(hash) % AVATAR_TONES.length]
  }, [name, tone])
  return (
    <span
      className={cx(styles.avatar, toneClass(picked), className)}
      style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.38)) }}
      title={title ?? name ?? undefined}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      aria-label={title}
    >
      {initials(name)}
    </span>
  )
}

/* ------------------------------------------------------------------------ */
/* key / value                                                              */
/* ------------------------------------------------------------------------ */

export interface KeyValueItem {
  label: ReactNode
  value: ReactNode
  /** Value in the mono face (ids, amounts). */
  mono?: boolean
  /** A caption under the value. */
  hint?: ReactNode
  /** Spans both columns in a two-column list. */
  wide?: boolean
}

/** A definition list with hairline rows; `columns={2}` for a dense summary. Falsy items are skipped. */
export function KeyValue({ items, columns = 1, labelWidth = 168, className }: { items: ReadonlyArray<KeyValueItem | null | false | undefined>; columns?: 1 | 2; labelWidth?: number; className?: string }): JSX.Element {
  return (
    <dl className={cx(styles.kvList, columns === 2 ? styles.kvTwo : '', className)} style={{ '--kv-label': `${labelWidth}px` } as CSSProperties}>
      {items.filter((item): item is KeyValueItem => Boolean(item)).map((item, i) => (
        <div key={i} className={cx(styles.kvRow, item.wide ? styles.kvWide : '')}>
          <dt className={styles.kvLabel}>{item.label}</dt>
          <dd className={cx(styles.kvValue, item.mono ? styles.num : '')}>
            {item.value}
            {item.hint ? <span className={styles.kvHint}>{item.hint}</span> : null}
          </dd>
        </div>
      ))}
    </dl>
  )
}
